import { useNavigate } from "react-router-dom";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import {
  findJobsForStep,
  mostRecentMatrixJobs,
  mostRecentParallelJobs,
  useBuild,
} from "~/components/build/Show/lib/BuildContext";
import { useStepDependenciesStore } from "~/components/build/Show/lib/useStepDependenciesStore";
import {
  FilterByOption,
  filterSteps,
} from "~/components/build/Show/components/Sidebar/useFilterStore";
import { isMatrixCommandStep, isParallelCommandStep } from "~/lib/pipeline";
import { CommandJob, Job } from "~/components/build/Show/lib/types";
import { useCallback, useEffect, useMemo } from "react";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { resolveJobUrl, resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  nextJobFailureQueryKey,
  nextJobFailureQueryOptions,
} from "~/components/build/Show/lib/queries/nextJobFailure";
import { jobQueryOptions } from "~/components/build/Show/lib/queries/job";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";

/**
 * Get the list of current failing steps (and jobs) in the build path.
 */
const useFailurePaths = (): { step: string; job?: string }[] => {
  const { build } = useBuild();
  const { steps } = useSteps();
  const dependenciesByStep = useStepDependenciesStore((state) => state.dependenciesByStep);

  return useMemo(() => {
    return (
      filterSteps(steps, [FilterByOption.Failed], build)
        .filter((step) => step.type !== "group")
        .flatMap((step) => {
          // Non-sidebar canvas only supports jumping to steps rather than jobs.
          if (!Features.BuildSidebar) {
            return { step: step.uuid };
          }

          // Find the latest parallel failed jobs for the step.
          if (isParallelCommandStep(step)) {
            const jobs = findJobsForStep<CommandJob>(build, step.uuid).filter(
              (job) => job.state === "finished" && !job.passed,
            );
            return mostRecentParallelJobs(jobs).map((job) => ({
              job: job.id,
              step: step.uuid,
            }));
          }

          // Find the latest matrix failed jobs for the step.
          if (isMatrixCommandStep(step)) {
            const jobs = findJobsForStep<CommandJob>(build, step.uuid).filter(
              (job) => job.state === "finished" && !job.passed,
            );
            return mostRecentMatrixJobs(jobs).map((job) => ({
              job: job.id,
              step: step.uuid,
            }));
          }

          return { step: step.uuid };
        }) || []
    );
  }, [steps, build?.jobs, dependenciesByStep]);
};

type UseFocusOnFailureReturn = {
  hasNextFailure: boolean;
  focusOnFailure: () => void;
};

// Used as a magic value to represent the last job in the build (therefore making the 'next failure'
// the first failure in the build.
const ZERO_UUID = "00000000-0000-0000-0000-000000000000";

export const useFocusOnFailure = (): UseFocusOnFailureReturn => {
  const { build } = useBuild();
  const navigate = useNavigate();

  // Features.ProgressiveBuildShow off (legacy)
  const { stepUuid, jobUuid } = useQueryParams();
  const failures = useFailurePaths();

  // Features.ProgressiveBuildShow on
  const queryClient = useQueryClient();
  const { currentLatestJobUuid, currentJobUuid } = useCurrentJobOrStep();

  const nextFailuresQuery = useQuery(
    nextJobFailureQueryOptions(build, currentLatestJobUuid || currentJobUuid || ZERO_UUID, {
      refetchOnWindowFocus: true,
    }),
  );

  // Preload the next failure
  useEffect(() => {
    if (Features.ProgressiveBuildShow && nextFailuresQuery.data) {
      // Check if the next failure is already in the cache
      const nextFailureKey = nextJobFailureQueryKey(
        build.id,
        nextFailuresQuery.data,
        build.statistics?.jobsFailedCount ?? 0,
      );
      const cacheData = queryClient.getQueryData(nextFailureKey) as Job["id"] | null;
      if (cacheData) {
        // We've got the next failure, let's get the next next one too (so we are essentially loading the next two)
        queryClient.ensureQueryData(nextJobFailureQueryOptions(build, cacheData));
        queryClient.ensureQueryData(jobQueryOptions(build, cacheData));
      } else {
        queryClient.ensureQueryData(nextJobFailureQueryOptions(build, nextFailuresQuery.data));
        queryClient.ensureQueryData(jobQueryOptions(build, nextFailuresQuery.data));
      }
    }
  }, [nextFailuresQuery.data]);

  const nextFailureUrl = useMemo(() => {
    if (!failures.length) {
      return null;
    }

    let nextFailure: { step: string; job?: string };

    const current = failures.find((failure) => {
      if (jobUuid) {
        return failure.job === jobUuid;
      }
      if (stepUuid) {
        return failure.step === stepUuid;
      }
      return false;
    });

    if (!current) {
      nextFailure = failures[0];
    } else {
      const idx = failures.indexOf(current);

      if (idx + 1 < failures.length) {
        nextFailure = failures[idx + 1];
      } else {
        nextFailure = failures[0];
      }
    }

    // Navigate to the step or job based on the failure.
    if (nextFailure.job) {
      return resolveJobUrl(nextFailure.job);
    }

    return resolveStepUrl(nextFailure.step);
  }, [failures, stepUuid, jobUuid, navigate]);

  const focusOnFailureEager = useCallback(() => {
    if (!Features.ProgressiveBuildShow && nextFailureUrl) {
      navigate(nextFailureUrl, { state: { forceFocus: true } });
    }
  }, [nextFailureUrl]);

  const focusOnFailureLazy = useCallback(() => {
    if (Features.ProgressiveBuildShow && nextFailuresQuery.data) {
      navigate(resolveJobUrl(nextFailuresQuery.data), { state: { forceFocus: true } });
    }
  }, [nextFailuresQuery.data]);

  if (Features.ProgressiveBuildShow) {
    return {
      hasNextFailure: Boolean(nextFailuresQuery.data),
      focusOnFailure: focusOnFailureLazy,
    };
  }

  return {
    hasNextFailure: Boolean(nextFailureUrl),
    focusOnFailure: focusOnFailureEager,
  };
};
